import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import ServiceStatusCard from './ServiceStatusCard';
import LoadingCard from './LoadingCard';

// Type definitions for the data received from the API
type Status = "Operational" | "Degraded Performance" | "Partial Outage" | "Major Outage" | "Unknown";

interface ServiceStatus {
  name: string;
  status: Status;
  last_checked: string;
}

const ServiceDashboard: React.FC = () => {
  const [serviceStatuses, setServiceStatuses] = useState<ServiceStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [, setLastUpdate] = useState<Date>(new Date());

  const fetchStatus = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setIsRefreshing(true);
    }
    
    try {
      // Use a relative path for the API call to work in production
      const response = await axios.get<ServiceStatus[]>('/api/status');
      setServiceStatuses(response.data);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError("Failed to fetch service statuses. Is the backend server running?");
      console.error(err);
    } finally {
      setLoading(false);
      if (isManualRefresh) {
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  };

  useEffect(() => {
    fetchStatus(); // Initial fetch

    const intervalId = setInterval(() => fetchStatus(), 30000); // Poll every 30 seconds

    return () => clearInterval(intervalId); // Cleanup on component unmount
  }, []);

  const handleManualRefresh = () => {
    fetchStatus(true);
  };

  const getOverallStatus = () => {
    if (serviceStatuses.length === 0) return 'unknown';
    const hasOutage = serviceStatuses.some(s => s.status === 'Major Outage' || s.status === 'Partial Outage');
    const hasDegraded = serviceStatuses.some(s => s.status === 'Degraded Performance');
    
    if (hasOutage) return 'outage';
    if (hasDegraded) return 'degraded';
    return 'operational';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
            Service Status
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <LoadingCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass backdrop-blur-sm rounded-2xl p-8 border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/20"
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-xl">
            <WifiOff className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
              Connection Error
            </h3>
            <p className="text-red-600 dark:text-red-400 text-sm">
              {error}
            </p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Retry</span>
        </motion.button>
      </motion.div>
    );
  }

  const overallStatus = getOverallStatus();

  return (
    <div className="space-y-6">
      {/* Header with overall status */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
            Service Status
          </h2>
          <div className="flex items-center space-x-2">
            {overallStatus === 'operational' && (
              <div className="flex items-center space-x-2 text-emerald-600 dark:text-emerald-400">
                <Wifi className="w-5 h-5" />
                <span className="text-sm font-medium">All Systems Operational</span>
              </div>
            )}
            {overallStatus === 'degraded' && (
              <div className="flex items-center space-x-2 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm font-medium">Some Issues Detected</span>
              </div>
            )}
            {overallStatus === 'outage' && (
              <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                <WifiOff className="w-5 h-5" />
                <span className="text-sm font-medium">Service Disruption</span>
              </div>
            )}
          </div>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleManualRefresh}
          disabled={isRefreshing}
          className="p-3 rounded-xl bg-white/10 dark:bg-black/10 backdrop-blur-sm border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-black/20 transition-all duration-200 shadow-lg disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 text-slate-600 dark:text-slate-300 ${isRefreshing ? 'animate-spin' : ''}`} />
        </motion.button>
      </motion.div>

      {/* Service cards grid */}
      <AnimatePresence mode="wait">
        {serviceStatuses.length > 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {serviceStatuses.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="transform transition-transform duration-200"
              >
                <ServiceStatusCard 
                  name={service.name} 
                  status={service.status} 
                  last_checked={service.last_checked} 
                />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass backdrop-blur-sm rounded-2xl p-12 text-center border border-white/20 dark:border-white/10"
          >
            <div className="space-y-4">
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-2xl inline-flex">
                <AlertTriangle className="w-8 h-8 text-slate-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  No Services Found
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Check the backend configuration to add services.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServiceDashboard;
